import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, SearchInput, tokens } from '@hotjar/hotsauce';

import type { Collection } from '@hotjar/webapp/domains/collections/model';
import {
  SearchInputWrapper,
  SideBarWrapper,
} from '@hotjar/insights-common/components/SideBar/SideBar';
import * as collectionsApi from '@hotjar/webapp/domains/collections/api';
import { useNavigation } from '@hotjar/navigation';
import { createCollectionLink } from './utils';
import { AddCollection } from '../AddCollection';

import { SidebarCollectionsList } from './SidebarCollectionsList';
import type { CollectionItemAction, CollectionListItem } from './CollectionsListItem';

import { RemovalConfirmation } from '../RemovalConfirmation';

const AddNewCollection = styled(Button)`
  margin: ${tokens.spacer.x1pt5} 0;
  svg,
  span {
    color: ${tokens.color.link};
  }
`;

interface CollectionSidebarProps {
  collections: Collection[] | undefined;
  currentSiteId: string | null;
  setSearchQuery: (name: string) => void;
  selectedCollectionId: string | undefined;
  defaultAllHighlights: Collection;
}

export const CollectionsSidebar = ({
  collections,
  currentSiteId,
  setSearchQuery,
  selectedCollectionId,
  defaultAllHighlights,
}: CollectionSidebarProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { topBarHeight: topNavHeight } = useNavigation();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [collectionItems, setCollectionItems] = useState(
    collections?.map((collection) => ({ ...collection, isEditing: false })),
  );
  const [isRemovalConfirmationOpen, setIsRemovalConfirmationOpen] = useState(false);
  const [confirmRemovalOfCollection, setConfirmRemovalOfCollection] =
    useState<CollectionListItem | null>(null);

  useEffect(() => {
    setCollectionItems(collections?.map((collection) => ({ ...collection, isEditing: false })));
  }, [collections, setCollectionItems]);

  const onCreateCollectionSuccess = (newCollectionId: string): void => {
    if (currentSiteId) {
      navigate(createCollectionLink(currentSiteId, newCollectionId));
    }
  };

  const markRemovalConfirmationAsClosed = useCallback(() => {
    setIsRemovalConfirmationOpen(false);
  }, [setIsRemovalConfirmationOpen]);

  const saveItem = async (item: CollectionListItem) => {
    if (currentSiteId) {
      await collectionsApi.rename(currentSiteId, item.id, item.name);
      setCollectionItems(
        collectionItems?.map((collection) =>
          collection.id === item.id
            ? { ...collection, name: item.name, isEditing: false }
            : collection,
        ),
      );
    }
  };

  const onClearEdit = () => {
    setCollectionItems(collectionItems?.map((collection) => ({ ...collection, isEditing: false })));
  };

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const actions: CollectionItemAction[] = [
    {
      icon: 'text_field_short',
      name: 'edit',
      label: t('collections:sidebar.actions.rename'),
      cb: (editingItem) => {
        setCollectionItems(
          collectionItems?.map((item) => ({ ...item, isEditing: item.id === editingItem.id })),
        );
      },
    },
    {
      icon: 'trash',
      color: 'text_warning',
      name: 'delete',
      label: t('collections:sidebar.actions.delete'),
      cb: (item) => {
        setConfirmRemovalOfCollection(item);
        setIsRemovalConfirmationOpen(true);
      },
    },
  ];

  return (
    <SideBarWrapper topNavHeight={topNavHeight}>
      <SearchInputWrapper>
        <SearchInput onChange={onSearch} placeholder={t('collections:sidebar.jump-to')} size="s" />
      </SearchInputWrapper>

      <AddNewCollection
        kind="ghost"
        icon="plus"
        aria-label={t('collections:sidebar.create-new-collection')}
        onClick={() => setCreateModalOpen(true)}
      >
        {t('collections:sidebar.create-new-collection')}
      </AddNewCollection>

      <SidebarCollectionsList
        defaultAllHighlights={defaultAllHighlights}
        selectedCollectionId={selectedCollectionId}
        errorStateText="Error"
        emptyStateText="Empty"
        items={collectionItems ?? []}
        state="ready"
        actions={actions}
        onItemSave={saveItem}
        onItemBlur={onClearEdit}
      />
      {currentSiteId !== null && (
        <AddCollection
          isOpen={isCreateModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={onCreateCollectionSuccess}
          siteId={currentSiteId}
        />
      )}
      {isRemovalConfirmationOpen && confirmRemovalOfCollection && (
        <RemovalConfirmation
          collectionId={confirmRemovalOfCollection.id}
          collectionName={confirmRemovalOfCollection.name}
          isOpen={!!confirmRemovalOfCollection}
          onClose={markRemovalConfirmationAsClosed}
          siteId={currentSiteId}
        />
      )}
    </SideBarWrapper>
  );
};
